<template>
  <div class="json-editor-wrapper">
    <div v-if="!disabled" class="view-json-button" @click="openJsonModal()">
      <VdIcon store="fa" name="arrow-up-right-from-square" color="var(--vd-color-blue-default)" />
    </div>
    <div class="json-value" v-html="formatJsonValue(modelValue || '{}')"></div>

    <JsonEditorModal
      v-bind="attrs"
      :is-visible="isJsonModalVisible"
      :json-content="modelValue || '{}'"
      :readonly="readonly"
      @close="handleModalClose"
    />
  </div>
</template>

<script setup lang="ts">
import { VdIcon } from "@voodoo.io/voodoo-ui";
import { ref, useAttrs } from "vue";

import JsonEditorModal from "./JsonEditorModal.vue";

const attrs = useAttrs();

const props = defineProps({
  modelValue: {
    type: String,
    default: "{}",
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const isJsonModalVisible = ref(false);

const formatJsonValue = (value: string) => {
  try {
    const parsedValue = JSON.parse(value);

    if (Object.keys(parsedValue).length === 0) {
      return `<span style="color: #999; margin-left: 0.5rem">${props.readonly ? "Empty" : "Edit"} JSON</span>`;
    }

    const jsonString = JSON.stringify(parsedValue, null, 2);

    const formattedJson = jsonString
      .replace(/"([^"]+)":/g, '<span style="color: #1a1a1a;">"$1"</span>:') // Color keys
      .replace(/: (\d+)/g, ': <span style="color: #ee422e;">$1</span>') // Color numbers
      .replace(/: (true|false)/g, ': <span style="color: #ff8c00;">$1</span>') // Color booleans
      .replace(/: "([^"]+)"/g, ': <span style="color: #008000;">"$1"</span>') // Color string values
      .replace(/: null/g, ': <span style="color: #004ed0;">null</span>'); // Color null values

    return `<pre>${formattedJson}</pre>`;
  } catch (error) {
    console.error("Invalid JSON:", error);
    return `<span style="color: red;">Invalid JSON</span>`;
  }
};

const openJsonModal = () => {
  isJsonModalVisible.value = true;
};

const handleModalClose = () => {
  isJsonModalVisible.value = false;
};
</script>

<style lang="scss">
.json-editor-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.json-value {
  overflow-y: auto;
  padding: 0.25rem;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  width: 100%;
  min-height: 40px;
  display: flex;
  align-items: center;

  pre {
    overflow-y: auto;
    font-family: monospace;
    overflow: hidden;
    max-height: 72px;
    margin: 0;
  }

  &.json-modal-content {
    padding: 0.5rem;
    max-height: 85%;

    pre {
      max-height: 100%;
    }
  }
}

.view-json-button {
  position: absolute;
  right: 0.25rem;
  top: 0.25rem;
  cursor: pointer;
  background-color: var(--vd-color-blue-light-9);
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--vd-color-blue-light-3);
  color: var(--vd-color-blue-default);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
}
</style>
